import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  ApiError,
  apiClient,
  type ExecutionApproval,
  type ExecutionRun,
  type ExecutionRunDetail,
} from "../api/client";
import { useAuth } from "../app/AuthContext";

const terminalStates = new Set(["completed", "failed", "cancelled"]);

export function RunsPage() {
  const { runId } = useParams();
  const { session } = useAuth();
  const [runs, setRuns] = useState<ExecutionRun[]>([]);
  const [detail, setDetail] = useState<ExecutionRunDetail | null>(null);
  const [error, setError] = useState("");
  const [streamStatus, setStreamStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshRuns = useCallback(async () => {
    try {
      setRuns(await apiClient.listExecutionRuns());
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }, []);

  const refreshDetail = useCallback(async () => {
    if (!runId) return;
    try {
      setDetail(await apiClient.getExecutionRun(runId));
      setError("");
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }, [runId]);

  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  useEffect(() => {
    setDetail(null);
    void refreshDetail();
  }, [refreshDetail]);

  useRunStream(runId, detail, refreshDetail, setStreamStatus);

  async function cancelRun() {
    if (!detail) return;
    setBusy(true);
    try {
      await apiClient.cancelExecutionRun(detail.id);
      await Promise.all([refreshDetail(), refreshRuns()]);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function decide(
    approval: ExecutionApproval,
    decision: "approved" | "rejected",
    note: string,
  ) {
    if (!detail) return;
    setBusy(true);
    try {
      await apiClient.decideExecutionApproval(detail.id, approval.approval_key, decision, note);
      await Promise.all([refreshDetail(), refreshRuns()]);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack runs-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">Skill · Workflow · Agent</span>
          <h1>运行记录</h1>
          <p>查看版本固定的执行模式、实时事件、审批门、取消状态和只读制品。</p>
        </div>
        <button className="secondary-button" onClick={() => void refreshRuns()} type="button">
          刷新历史
        </button>
      </header>
      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}
      <div className="run-center-layout">
        <RunList runs={runs} selectedId={runId} />
        {runId ? (
          detail ? (
            <RunDetail
              busy={busy}
              canCancel={detail.requested_by_user_id === session?.user_id}
              detail={detail}
              onCancel={cancelRun}
              onDecide={decide}
              role={session?.role ?? "user"}
              streamStatus={streamStatus}
            />
          ) : (
            <section className="flow-panel run-detail-loading">正在加载运行详情…</section>
          )
        ) : (
          <section className="flow-panel run-detail-empty">
            <span className="eyebrow">选择一个运行</span>
            <h2>查看实时进度与完整记录</h2>
            <p>运行列表为空时，可先从任务中心启动 Skill、Workflow 或领域 Agent。</p>
          </section>
        )}
      </div>
    </div>
  );
}

function RunList({ runs, selectedId }: { runs: ExecutionRun[]; selectedId?: string }) {
  return (
    <section aria-labelledby="run-history-title" className="run-history-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">最近运行</span>
          <h2 id="run-history-title">历史</h2>
        </div>
        <span className="phase-badge">{runs.length}</span>
      </div>
      <div className="run-history-list">
        {runs.map((run) => (
          <Link
            aria-current={run.id === selectedId ? "page" : undefined}
            className={run.id === selectedId ? "run-history-item active" : "run-history-item"}
            key={run.id}
            to={`/runs/${run.id}`}
          >
            <div>
              <span className={`status-pill ${statusClass(run.state)}`}>
                {stateLabel(run.state)}
              </span>
              <strong>{run.target}</strong>
            </div>
            <small>
              {modeLabel(run.execution_mode)} · {formatTime(run.created_at)}
            </small>
          </Link>
        ))}
        {!runs.length && <p className="panel-copy">暂无当前授权范围内的运行。</p>}
      </div>
    </section>
  );
}

function RunDetail({
  detail,
  role,
  streamStatus,
  busy,
  canCancel,
  onCancel,
  onDecide,
}: {
  detail: ExecutionRunDetail;
  role: "user" | "manager" | "owner";
  streamStatus: string;
  busy: boolean;
  canCancel: boolean;
  onCancel: () => Promise<void>;
  onDecide: (
    approval: ExecutionApproval,
    decision: "approved" | "rejected",
    note: string,
  ) => Promise<void>;
}) {
  const cancellable = !terminalStates.has(detail.state) && canCancel;
  return (
    <section className="flow-panel run-detail-panel" aria-labelledby="run-detail-title">
      <div className="run-detail-heading">
        <div>
          <span className="eyebrow">{modeLabel(detail.execution_mode)}</span>
          <h2 id="run-detail-title">{detail.target}</h2>
          <small>{detail.id}</small>
        </div>
        <span className={`status-pill ${statusClass(detail.state)}`}>
          {stateLabel(detail.state)}
        </span>
      </div>
      <dl className="run-facts">
        <div>
          <dt>资源范围</dt>
          <dd>
            {detail.scope_type} · {detail.scope_id}
          </dd>
        </div>
        <div>
          <dt>尝试次数</dt>
          <dd>{detail.attempt_count}</dd>
        </div>
        <div>
          <dt>最新事件</dt>
          <dd>#{detail.last_event_seq}</dd>
        </div>
        <div>
          <dt>创建时间</dt>
          <dd>{formatTime(detail.created_at)}</dd>
        </div>
      </dl>
      {streamStatus && !terminalStates.has(detail.state) && (
        <p className="stream-status" role="status">
          {streamStatus}
        </p>
      )}
      {detail.safe_error && (
        <div className="alert error" role="alert">
          {safeError(detail.safe_error)}
        </div>
      )}
      {cancellable && (
        <button
          className="danger-outline-button"
          disabled={busy}
          onClick={() => void onCancel()}
          type="button"
        >
          取消运行
        </button>
      )}
      <ApprovalList approvals={detail.approvals} busy={busy} onDecide={onDecide} role={role} />
      <StepList detail={detail} />
      <ArtifactList artifacts={detail.artifacts} />
      <EventList events={detail.events} />
    </section>
  );
}

function ApprovalList({
  approvals,
  role,
  busy,
  onDecide,
}: {
  approvals: ExecutionApproval[];
  role: "user" | "manager" | "owner";
  busy: boolean;
  onDecide: (
    approval: ExecutionApproval,
    decision: "approved" | "rejected",
    note: string,
  ) => Promise<void>;
}) {
  if (!approvals.length) return null;
  return (
    <section className="run-subsection" aria-labelledby="approval-title">
      <h3 id="approval-title">人工审批</h3>
      {approvals.map((approval) => (
        <ApprovalCard
          approval={approval}
          busy={busy}
          key={approval.approval_key}
          onDecide={onDecide}
          role={role}
        />
      ))}
    </section>
  );
}

function ApprovalCard({
  approval,
  role,
  busy,
  onDecide,
}: {
  approval: ExecutionApproval;
  role: "user" | "manager" | "owner";
  busy: boolean;
  onDecide: (
    approval: ExecutionApproval,
    decision: "approved" | "rejected",
    note: string,
  ) => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const eligible = role === "owner" || (role === "manager" && approval.required_role === "manager");
  return (
    <article className="approval-card">
      <div>
        <strong>{approval.approval_key}</strong>
        <span>
          {approval.required_role} · {approval.decision}
        </span>
      </div>
      {approval.decision === "pending" && eligible && (
        <>
          <label>
            审批备注
            <textarea onChange={(event) => setNote(event.target.value)} value={note} />
          </label>
          <div className="review-actions">
            <button
              disabled={busy}
              onClick={() => void onDecide(approval, "approved", note)}
              type="button"
            >
              批准并继续
            </button>
            <button
              className="danger-button"
              disabled={busy}
              onClick={() => void onDecide(approval, "rejected", note)}
              type="button"
            >
              拒绝
            </button>
          </div>
        </>
      )}
    </article>
  );
}

function StepList({ detail }: { detail: ExecutionRunDetail }) {
  return (
    <section className="run-subsection" aria-labelledby="steps-title">
      <h3 id="steps-title">{detail.execution_mode === "agent" ? "Agent 计划" : "执行步骤"}</h3>
      {detail.execution_mode === "agent"
        ? detail.agent_plan.map((plan) => (
            <article className="run-step" key={plan.sequence}>
              <span>{plan.sequence}</span>
              <div>
                <strong>{plan.rationale_summary}</strong>
                <small>
                  {plan.state} · {plan.tool_calls.length} 次工具调用
                </small>
                <details>
                  <summary>查看计划动作与策略决定</summary>
                  <pre>
                    {JSON.stringify(
                      { planned_action: plan.planned_action, tool_calls: plan.tool_calls },
                      null,
                      2,
                    )}
                  </pre>
                </details>
              </div>
            </article>
          ))
        : detail.steps.map((step) => (
            <article className="run-step" key={step.id}>
              <span>{step.sequence}</span>
              <div>
                <strong>{step.step_key}</strong>
                <small>
                  {step.state} · 尝试 {step.attempt_count}/{step.max_attempts}
                </small>
                {(step.output_payload || step.safe_error) && (
                  <details>
                    <summary>查看步骤结果</summary>
                    <pre>
                      {JSON.stringify(
                        { output: step.output_payload, error: step.safe_error },
                        null,
                        2,
                      )}
                    </pre>
                  </details>
                )}
              </div>
            </article>
          ))}
      {!detail.steps.length && !detail.agent_plan.length && (
        <p className="panel-copy">正在等待执行器产生步骤记录。</p>
      )}
    </section>
  );
}

function ArtifactList({ artifacts }: { artifacts: ExecutionRunDetail["artifacts"] }) {
  if (!artifacts.length) return null;
  return (
    <section className="run-subsection" aria-labelledby="artifacts-title">
      <h3 id="artifacts-title">只读制品</h3>
      <div className="artifact-list">
        {artifacts.map((artifact) => (
          <a href={artifact.download_url} key={artifact.id}>
            <strong>{artifact.artifact_type}</strong>
            <small>
              {artifact.media_type} · {formatBytes(artifact.byte_size)}
            </small>
          </a>
        ))}
      </div>
    </section>
  );
}

function EventList({ events }: { events: ExecutionRunDetail["events"] }) {
  return (
    <details className="run-subsection event-details">
      <summary>事件时间线（{events.length}）</summary>
      <ol>
        {events.map((event) => (
          <li key={event.seq}>
            <span>#{event.seq}</span>
            <strong>{event.type}</strong>
            <time>{formatTime(event.timestamp)}</time>
          </li>
        ))}
      </ol>
    </details>
  );
}

function useRunStream(
  runId: string | undefined,
  detail: ExecutionRunDetail | null,
  refresh: () => Promise<void>,
  setStatus: (value: string) => void,
) {
  useEffect(() => {
    if (!runId || !detail || terminalStates.has(detail.state)) return;
    let active = true;
    let socket: WebSocket | null = null;
    let retryTimer: number | null = null;
    let cursor = detail.last_event_seq;

    async function connect() {
      try {
        setStatus("正在建立实时连接…");
        const issued = await apiClient.issueWebSocketTicket();
        if (!active) return;
        socket = new WebSocket(apiClient.runWebSocketUrl(runId!, cursor, issued.ticket));
        socket.onopen = () => setStatus("实时连接已建立");
        socket.onmessage = (message) => {
          const sequence = eventSequence(message.data);
          if (sequence !== null) cursor = Math.max(cursor, sequence);
          void refresh();
        };
        socket.onclose = () => {
          if (!active) return;
          setStatus("连接已中断，正在从最新事件自动恢复…");
          retryTimer = window.setTimeout(() => void connect(), 1200);
        };
      } catch {
        if (!active) return;
        setStatus("实时连接暂不可用，正在通过持久状态恢复…");
        await refresh();
        retryTimer = window.setTimeout(() => void connect(), 2000);
      }
    }

    void connect();
    return () => {
      active = false;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      socket?.close();
    };
  }, [detail?.state, refresh, runId, setStatus]);
}

function eventSequence(data: unknown): number | null {
  try {
    const event = JSON.parse(String(data)) as { seq?: unknown };
    return typeof event.seq === "number" && Number.isInteger(event.seq) ? event.seq : null;
  } catch {
    return null;
  }
}

function modeLabel(mode: ExecutionRun["execution_mode"]): string {
  return { skill: "直接 Skill", workflow: "固定 Workflow", agent: "领域 Agent" }[mode];
}

function stateLabel(state: string): string {
  return (
    {
      accepted: "已接受",
      queued: "排队中",
      running: "运行中",
      awaiting_approval: "等待审批",
      completed: "已完成",
      failed: "失败",
      cancelled: "已取消",
    }[state] ?? state
  );
}

function statusClass(state: string): string {
  if (state === "completed") return "success";
  if (state === "failed" || state === "cancelled") return "danger";
  if (state === "awaiting_approval") return "insufficient";
  return "reviewed";
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
}

function formatBytes(value: number): string {
  return value < 1024 ? `${value} B` : `${(value / 1024).toFixed(1)} KB`;
}

function safeError(error: Record<string, unknown>): string {
  return `运行失败：${String(error.code ?? "unknown_error")}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.status === 404 ? "运行不存在，或当前授权已失效。" : error.message;
  }
  return "暂时无法读取运行中心。";
}
